import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { doc, updateDoc, Timestamp } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useAuth } from '../contexts/AuthContext'
import { Gift, Check, X, Sparkles, Zap } from 'lucide-react'
import './FreeTrialModal.css'

export default function FreeTrialModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(false)
  const { currentUser, fetchUserProfile } = useAuth()
  const navigate = useNavigate()

  if (!isOpen) return null

  async function handleClaimTrial() {
    setLoading(true)
    try {
      // Set expiration to 30 days from now
      const expiresAt = new Date()
      expiresAt.setDate(expiresAt.getDate() + 30)

      await updateDoc(doc(db, 'users', currentUser.uid), {
        plan: 'starter',
        trialActive: true,
        expiresAt: Timestamp.fromDate(expiresAt)
      })

      // Refresh user profile in context
      await fetchUserProfile(currentUser.uid)
      
      onClose()
      navigate('/dashboard')
    } catch (error) {
      console.error('Error claiming trial:', error)
      alert('Failed to claim trial. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-glow-container">
        <div className="modal-content trial-modal glass-effect">
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>

          <div className="decorative-blur blur-top-left"></div>
          <div className="decorative-blur blur-bottom-right"></div>

          <div className="trial-badge">
            <Sparkles size={14} className="sparkle-icon" />
            <span>Limited Time Offer</span>
          </div>

          <div className="trial-header">
            <div className="gift-icon-container">
              <div className="gift-icon-ring"></div>
              <Gift size={42} className="gift-icon" />
            </div>
            <h2>Unlock Pro Features</h2>
            <p>Experience the full power of Digital vCard with a <span className="highlight-text">30-day free trial</span>. No credit card required.</p>
          </div>

          <div className="trial-card-container">
            <div className="trial-features-list">
              <div className="feature-item">
                <div className="feature-check">
                  <Check size={14} strokeWidth={3} />
                </div>
                <div>
                  <span className="feature-title">Premium Templates</span>
                  <span className="feature-desc">Access all premium designs</span>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-check">
                  <Check size={14} strokeWidth={3} />
                </div>
                <div>
                  <span className="feature-title">Advanced Analytics</span>
                  <span className="feature-desc">Track visits and clicks</span>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-check">
                  <Check size={14} strokeWidth={3} />
                </div>
                <div>
                  <span className="feature-title">Unlimited Sharing</span>
                  <span className="feature-desc">Share without limits</span>
                </div>
              </div>
            </div>
          </div>

          <div className="trial-value-proposition">
            <div className="price-tag">
              <span className="original-price">₹270 value</span>
              <span className="free-tag">FREE</span>
            </div>
            <p className="renewal-text">Then just ₹9/month. Cancel anytime.</p>
          </div>

          <div className="trial-actions">
            <button 
              className="btn-claim-trial"
              onClick={handleClaimTrial}
              disabled={loading}
            >
              <div className="btn-content">
                {loading ? <span className="spinner"></span> : (
                  <>
                    <Zap size={20} fill="currentColor" />
                    <span>Claim 30 Days Free</span>
                  </>
                )}
              </div>
              <div className="btn-shine"></div>
            </button>
            <button 
              className="btn-skip"
              onClick={onClose}
            >
              No thanks, I'll stay on the free plan
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
